<template>
  <model-form
    ref="modelForm"
    :model="showing"
    :title="bulkEditing ? 'Bulk Editing ' + showingSlots.length + ' Showings' : (`Edit Showing (${showing.id})`)"
    :submodel="true"
    modal-type="modal"
    :embedded-in-model-table="false"
    @apply-changes="applyChanges"
  >
    <div
      v-if="showing && showing.movie"
      class="column"
    >
      <q-card-section
        v-if="!bulkEditing"
        class="row q-pa-xs q-gutter-sm items-center"
      >
        <img
          class="poster-img col-auto"
          :src="showing.movie.posterImageSmallUrl"
        >
        <div class="col text-h4 text-">
          {{ showing.movie.name }}
        </div>
      </q-card-section>
      <q-card-section
        class="row q-px-xs items-center"
      >
        <q-chip
          v-if="showing.planElement"
          :style="'color: ' + showing.planElement.color + '; border-color: ' + showing.planElement.color + '; background-color: ' + showing.planElement.color + '33;'"
        >
          {{ showing.planElement.name }}
          <q-tooltip>
            <div class="text-overline2">
              Plan Element
            </div>
            <div class="text-h6">
              {{ showing.planElement.name }}
            </div>
          </q-tooltip>
        </q-chip>
        <q-chip
          v-if="showing.computedPriceCard"
          color="primary"
          text-color="white"
        >
          {{ showing.computedPriceCard.name }}
          <q-tooltip>
            <div class="text-overline2">
              Price Card
            </div>
            <div class="text-h6">
              {{ showing.computedPriceCard.name }}
            </div>
            <div class="text-body">
              Source: {{ showing.priceCardSource }}
            </div>
          </q-tooltip>
        </q-chip>
        <q-chip
          v-if="showing.movieTerm"
          color="secondary"
          text-color="white"
        >
          {{ showing.movieTerm.format }}
          <q-tooltip>
            <div class="text-overline2">
              Movie Term
            </div>
            <div class="text-h6">
              {{ showing.movieTerm.format }}
            </div>
            <div class="text-body">
              Source: Movie
            </div>
          </q-tooltip>
        </q-chip>
        <showing-badge
          v-for="showingBadge in showing.computedShowingBadges"
          :key="showingBadge.id"
          :showing-badge="showingBadge"
          outline
          color="dark"
        />
        <q-space />
        <q-icon
          v-if="showing.private"
          name="visibility_off"
          size="md"
          color="negative"
          class="q-px-sm"
        >
          <q-tooltip>
            This showing is private and will not show up on the website
          </q-tooltip>
        </q-icon>
        <q-icon
          v-if="showing.isPreview"
          name="preview"
          size="md"
          color="info"
          class="q-px-sm"
        >
          <q-tooltip>
            This showing is a preview screening and will not affect the release date.
          </q-tooltip>
        </q-icon>
        <q-icon
          v-if="showing.published || showing.ticketsSold > 0"
          name="lock"
          size="md"
          :style="showing.ticketsSold > 0 ? 'color: red;' : ''"
        >
          <q-tooltip>
            {{ showing.published ? 'Published' : '' }}{{ showing.published && showing.ticketsSold > 0 ? ', ' : '' }}
            {{ showing.ticketsSold }} ticket{{ showing.ticketsSold === 1 ? '' : 's' }} sold
          </q-tooltip>
        </q-icon>
      </q-card-section>

      <q-tabs
        v-if="showing.isMarathon"
        v-model="tab"
        style="max-width: 452px;"
        align="justify"
      >
        <q-tab
          name="marathon"
          label="Marathon"
        />
        <q-tab
          name="showings"
          label="Showings"
        />
      </q-tabs>
      <q-tab-panels
        v-model="tab"
        keep-alive
      >
        <q-tab-panel
          name="marathon"
        >
          <div
            v-if="showing.hasMarathon"
            class="row q-gutter-md items-start q-mb-sm"
          >
            <q-toggle
              v-model="showing.allowSalesInMarathon"
              stack
              class="col-auto q-pb-md"
              label="Allow Individual Sales"
              bottom-slots
              autofocus
            />
          </div>
          <div
            v-if="!showing.hasMarathon"
            class="row q-gutter-md items-start q-mt-sm"
          >
            <q-toggle
              v-model="showing.overrideReservedSeating"
              stack
              class="col-auto q-pb-md"
              label="Override Reserved Seating"
              bottom-slots
              autofocus
            />
            <q-checkbox
              v-if="showing.overrideReservedSeating"
              v-model="showing.overrideReservedSeatingValue"
              class="col"
              label="Reserved Seating"
              bottom-slots
            />
          </div>
          <div
            v-if="!showing.hasSeatChart && !showing.overrideSeatChart"
            class="row q-gutter-md items-center"
          >
            <q-toggle
              v-model="showing.overrideHeldSeatCount"
              stack
              class="col-auto q-pb-md"
              label="Override"
              bottom-slots
              autofocus
            />
            <q-input
              v-model.number="showing.customHeldSeatCount"
              type="number"
              class="col"
              label="Held Seat Count"
              min="0"
              :max="showing.screen.seatCount"
              :disable="!showing.overrideHeldSeatCount"
              :rules="[
                val => val !== '' || 'Field is required',
                val => val >= 0 || 'Cannot be less than 0' ,
                val => val <= showing.screen.seatCount || 'Must not exceed the total seat count'
              ]"
              bottom-slots
            />
          </div>
          <div
            v-if="!showing.hasSeatChart && !showing.overrideSeatChart && (showing.isMarathon || showing.hasMarathon)"
            class="row q-gutter-md items-center"
          >
            <q-toggle
              v-model="showing.overrideMarathonSeatCount"
              stack
              class="col-auto q-pb-md"
              label="Override"
              bottom-slots
              autofocus
            />
            <q-input
              v-model.number="showing.customMarathonSeatCount"
              type="number"
              class="col"
              label="Marathon Seat Count"
              min="0"
              :max="showing.screen.seatCount - showing.heldSeatCount"
              :disable="!showing.overrideMarathonSeatCount"
              :rules="[
                val => val !== '' || 'Field is required',
                val => val >= 0 || 'Cannot be less than 0' ,
                val => val <= (showing.screen.seatCount - showing.heldSeatCount) || 'Must not exceed the total seat count less held seats'
              ]"
              bottom-slots
            />
          </div>
          <div class="row q-gutter-md items-center">
            <q-toggle
              v-model="showing.overridePriceCard"
              stack
              class="col-auto q-pb-md"
              label="Override"
              bottom-slots
              autofocus
            />
            <q-select
              v-model="showing.customPriceCard"
              class="col"
              label="Custom Price Card"
              :disable="!showing.overridePriceCard"
              :options="$_.filter(priceCards.data, priceCard => !priceCard.isArchived)"
              option-label="name"
              :display-value="showing.customPriceCard.name"
              bottom-slots
            />
          </div>
          <div class="row q-gutter-md items-center">
            <q-toggle
              v-model="showing.overrideShowingBadges"
              stack
              class="col-auto"
              label="Override"
              bottom-slots
              autofocus
            />
            <showing-badge-select
              v-if="!showing.overrideShowingBadges"
              :model="showing"
              label="Additional Showing Badges"
              field-name="additionalShowingBadgeIds"
              class="col"
              bottom-slots
            />
            <showing-badge-select
              v-else
              :model="showing"
              label="Custom Showing Badges"
              field-name="customShowingBadgeIds"
              class="col"
              bottom-slots
            />
          </div>
          <div
            v-if="!showing.hasMarathon"
            class="row q-gutter-md items-center"
          >
            <q-toggle
              v-model="showing.overrideSeatChart"
              stack
              class="col-auto q-pb-md"
              label="Override"
              bottom-slots
              autofocus
            />
            <q-select
              v-model="showing.customSeatChart"
              class="col"
              label="Seat Chart"
              :disable="!showing.overrideSeatChart"
              :options="seatCharts.data"
              option-label="name"
              :display-value="showing.customSeatChart ? showing.customSeatChart.name : ''"
              bottom-slots
              :rules="[val => !showing.overrideSeatChart || val !== null || 'Field is required']"
            />
          </div>
          <div class="row q-gutter-md items-center">
            <q-toggle
              v-model="showing.overridePostStartTimeBufferMinutes"
              stack
              class="col-auto q-pb-md"
              label="Override"
              bottom-slots
              autofocus
            />
            <q-input
              v-model.number="showing.customPostStartTimeBufferMinutes"
              :disable="!showing.overridePostStartTimeBufferMinutes"
              type="number"
              min="0"
              max="100"
              class="col"
              label="Post-Start Time Buffer (Minutes)"
              :rules="[val => !!val || val === 0 || 'Field is required']"
              bottom-slots
            />
          </div>
          <div class="row q-gutter-md items-center">
            <q-toggle
              v-model="showing.overrideMaxTicketsPerOrderPerShowing"
              class="col-auto q-pb-md"
              label="Override"
            />
            <q-input
              v-model.number="showing.maxTicketsPerOrderPerShowing"
              :disable="!showing.overrideMaxTicketsPerOrderPerShowing"
              type="number"
              min="0"
              step="1"
              class="col"
              label="Max Tickets Per Order Per Showing"
              :rules="[val => !!val || 'Field is required']"
              bottom-slots
            />
          </div>
          <div
            class="row q-gutter-md items-start q-mt-sm"
          >
            <q-checkbox
              v-if="!showing.hasMarathon"
              v-model="showing.private"
              class="col-3"
              label="Private Showing"
              bottom-slots
            />
            <q-checkbox
              v-if="!showing.hasMarathon"
              v-model="showing.isPreview"
              class="col-3"
              label="Preview Screening"
              bottom-slots
            />
            <q-checkbox
              v-if="currentSite.membershipRequired"
              v-model="showing.allowWithoutMembership"
              class="col-3"
              label="Allow Without Membership"
              bottom-slots
            />
            <q-checkbox
              v-if="currentSite.allowConsumerRefunds"
              v-model="showing.allowConsumerRefunds"
              class="col-3"
              label="Allow Consumer Refunds"
              bottom-slots
            />
            <q-checkbox
              v-if="currentSite.theaterSeatDeliveryAvailableOnline"
              v-model="showing.disableTheaterSeatDelivery"
              class="col-3"
              label="Disable Seat Delivery"
              bottom-slots
            >
              <q-tooltip>
                Allow online concession purchases for this showing to be delivered to the theater seat. When disabled, another delivery type must be available for online concession purchases.
              </q-tooltip>
            </q-checkbox>
            <q-checkbox
              v-if="currentSite.allowItemOrdersOnline && currentSite.theaterSeatDeliveryEnabled"
              v-model="showing.qrItemOrderingOnly"
              class="col"
              label="Restrict Online Ordering to QR Codes"
              bottom-slots
            >
              <q-tooltip>
                When enabled, online orders for this showing will only be available for delivery to the theater seat. Users will not be prompted to purchase concessions items during showing checkout.
              </q-tooltip>
            </q-checkbox>
            <q-checkbox
              v-if="currentSite.allowItemOrdersOnline"
              v-model="showing.allowConsumerQrTabWithoutPaymentMethod"
              class="col"
              label="Allow Open QR Tabs Without Payment Method"
              bottom-slots
            >
              <q-tooltip>
                Allows a tab to be started with a QR code without requiring a payment method to be added.
              </q-tooltip>
            </q-checkbox>
          </div>
        </q-tab-panel>
        <q-tab-panel
          name="showings"
        >
          <q-table
            v-model:pagination="pagination"
            :columns="showingColumns"
            :rows="showing.showings.data"
            :rows-per-page-options="[0]"
            hide-pagination
            bordered
            flat
            wrap-cells
            row-key="id"
          >
            <template #body-cell="props">
              <q-td
                :props="props"
              >
                <template v-if="props.col.component === 'checkbox'">
                  <q-checkbox
                    disable
                    :model-value="props.value"
                  />
                </template>
                <template v-else>
                  {{ props.value }}
                </template>
              </q-td>
            </template>

            <template
              #body-cell-actions="props"
            >
              <q-td
                :props="props"
              >
                <slot
                  name="row-actions"
                  v-bind="props"
                >
                  <div class="row q-gutter-sm justify-end no-wrap">
                    <slot
                      name="prepend-row-actions"
                      v-bind="props"
                    />
                    <q-btn
                      :hover="false"
                      label="Edit"
                      @click="$refs.marathonShowingForm.show(props.row)"
                    />
                  </div>
                </slot>
              </q-td>
            </template>
          </q-table>
        </q-tab-panel>
      </q-tab-panels>
    </div>
    <showing-form
      ref="marathonShowingForm"
      name="marathonShowingForm"
    />
  </model-form>
</template>
<script>

import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import ShowingBadge from 'src/modules/admin/showing-badge/components/ShowingBadge'
import ShowingBadgeSelect from 'src/modules/admin/showing-badge/components/ShowingBadgeSelect.vue'
import { defineAsyncComponent } from 'vue'

export default {
  name: 'ShowingForm',
  components: {
    ModelForm,
    ShowingBadge,
    ShowingBadgeSelect,
    ShowingForm: defineAsyncComponent(() => import('src/modules/admin/booking-week/ShowingForm'))
  },
  configuration: ['currentSite', 'priceCards', 'showingBadges'],
  models: {
    showing: {
      movie: {
        showingBadges: 'default'
      },
      movieTerm: {
        showingBadges: 'default'
      },
      showings: {
        movie: {
          showingBadges: 'default'
        },
        movieTerm: {
          showingBadges: 'default'
        },
        screen: {
          showingBadges: 'default'
        },
        customPriceCard: 'default',
        additionalShowingBadges: 'default',
        customShowingBadges: 'default'
      },
      screen: {
        showingBadges: 'default'
      },
      bookingWeek: {
        planElements: 'default'
      },
      customPriceCard: 'default',
      additionalShowingBadges: 'default',
      customShowingBadges: 'default',
      customSeatChart: 'default'
    },
    seatCharts: 'default'
  },
  data () {
    return {
      bulkEditing: false,
      showingSlots: [],
      tab: 'marathon',
      showingColumns: [
        { name: 'description', align: 'left', label: 'Description', field: 'description' },
        { name: 'sales', align: 'center', label: 'Sales', field: 'allowSalesInMarathon', component: 'checkbox' },
        { name: 'actions', label: 'Actions', align: 'center' }
      ],
      pagination: {
        page: 1,
        rowsPerPage: 0
      }
    }
  },
  methods: {
    setCollection (collection) {
      this.$refs.modelForm.model.setParent(collection)
    },
    show (model, initialData) {
      this.$refs.modelForm.show(model, initialData)
    },
    applyChanges () {
      if (this.bulkEditing && this.showingSlots.length > 1) {
        _.each(this.showingSlots, showingSlot => {
          _.each([
            'overrideReservedSeating',
            'overrideReservedSeatingValue',
            'overridePriceCard',
            'customPriceCard',
            'additionalShowingBadges',
            'overrideShowingBadges',
            'customShowingBadges',
            'overrideSeatChart',
            'customSeatChart',
            'overrideHeldSeatCount',
            'customHeldSeatCount',
            'overrideMaxTicketsPerOrderPerShowing',
            'maxTicketsPerOrderPerShowing',
            'overridePostStartTimeBufferMinutes',
            'customPostStartTimeBufferMinutes',
            'private',
            'isPreview',
            'allowWithoutMembership'
          ], field => {
            showingSlot.showing[field] = this.showing[field]
          })
        })
      }
    }
  }
}

</script>

<style lang="scss" scoped>
  .poster-img {
    width: 60px;
    height: 80px;
  }
</style>
